import { gql } from '@apollo/client';
import { BoxProps } from '@chakra-ui/react';
import { DocumentNode } from 'graphql';
import * as React from 'react';

import { Card } from '@/imports/ui/chakra/components/Card';

import { Post_PostV2Fragment } from './graphql-types/Post_PostV2Fragment';

import { Answer } from './components/Answer';
import { Blog } from './components/Blog';
import { DeletedHeader } from './components/DeletedHeader';
import { EventAlert } from './components/EventAlert';
import { Moderation } from './components/Moderation';
import { ModerationHeader } from './components/ModerationHeader';
import { PinnedHeader } from './components/PinnedHeader';
import { Poll } from './components/Poll';
import { Question } from './components/Question';
import { SimplePost } from './components/SimplePost';
import { useModeration } from './hooks/useModeration';

type SimplePostProps = React.ComponentProps<typeof SimplePost>;
type PollProps = React.ComponentProps<typeof Poll>;
type QuestionProps = React.ComponentProps<typeof Question>;
type AnswerProps = React.ComponentProps<typeof Answer>;
type BlogProps = React.ComponentProps<typeof Blog>;

type IPostProps = {
  post: Post_PostV2Fragment;
  options?: SimplePostProps['options'] &
    PollProps['options'] &
    QuestionProps['options'] &
    AnswerProps['options'] &
    BlogProps['options'];
};

export const Post: React.FC<IPostProps & BoxProps> & {
  postFragment: DocumentNode;
} = ({ post, options, children, ...boxProps }) => {
  const { shouldHidePost, shouldShowHeader } = useModeration({
    post,
    isAnswerHeader: false,
  });

  return shouldHidePost ? (
    <Moderation post={post} />
  ) : (
    <Card w="full" {...boxProps}>
      {((post.__typename !== 'AnswerV2' && !!post.deleted) ||
        (post.__typename === 'AnswerV2' && post.questionPost?.deleted)) && (
        <DeletedHeader post={post} isAnswerHeader={false} />
      )}
      {shouldShowHeader && (
        <ModerationHeader post={post} isAnswerHeader={false} />
      )}
      {post.__typename !== 'AnswerV2' && <PinnedHeader post={post} />}
      {post.__typename === 'SimplePost' ? (
        <SimplePost simplePost={post} options={options} />
      ) : post.__typename === 'PollV2' ? (
        <Poll poll={post} options={options} />
      ) : post.__typename === 'QuestionV2' ? (
        <Question question={post} options={options} />
      ) : post.__typename === 'AnswerV2' ? (
        <Answer answer={post} options={options} />
      ) : post.__typename === 'BlogV2' ? (
        <Blog blog={post} options={options} />
      ) : post.__typename === 'EventAlert' ? (
        <EventAlert eventAlert={post} />
      ) : null}
      {children}
    </Card>
  );
};

Post.postFragment = gql`
  fragment Post_PostV2Fragment on PostV2 {
    _id
    deleted
    ... on SimplePost {
      _id
      ...SimplePost_SimplePostFragment
    }
    ... on BlogV2 {
      _id
      ...Blog_BlogV2Fragment
    }
    ... on PollV2 {
      _id
      ...Poll_PollV2Fragment
    }
    ... on QuestionV2 {
      _id
      ...Question_QuestionV2Fragment
    }
    ... on AnswerV2 {
      _id
      questionPost {
        deleted
      }
      ...Answer_AnswerV2Fragment
    }
    ...EventAlert_PostFragment
    ...PostHeader_PostFragment
    ...ModerationHeader_PostFragment
    ...Moderation_PostFragment
    ...useModeration_PostFragment
    ...PinnedHeader_PostFragment
    ...DeletedHeader_PostFragment
  }
  ${SimplePost.simplePostFragment}
  ${Blog.blogFragment}
  ${Poll.pollFragment}
  ${Question.questionFragment}
  ${Answer.answerFragment}
  ${EventAlert.postFragment}
  ${ModerationHeader.postFragment}
  ${Moderation.postFragment}
  ${useModeration.postFragment}
  ${PinnedHeader.postFragment}
  ${DeletedHeader.postFragment}
`;
